import { arrayBufferToBase64, type ChatFileChunkConfig } from './chat-file-upload'

type Request = (body: Record<string, unknown>, signal: AbortSignal) => Promise<any>

export const uploadChatFileInChunks = async (input: {
  file: File
  attemptId: string
  agentId: string
  config: ChatFileChunkConfig
  request: Request
  isCancelled: () => boolean
  onProgress: (loaded: number, total: number, speed: number) => void
  wait?: (ms: number) => Promise<void>
}) => {
  const { file, config } = input
  if (
    !Number.isSafeInteger(config.chunk_bytes) ||
    config.chunk_bytes <= 0 ||
    config.chunk_bytes > 2 * 1024 * 1024 ||
    !Number.isSafeInteger(config.max_file_bytes) ||
    file.size <= 0 ||
    file.size > config.max_file_bytes
  ) {
    throw new Error('Некорректный размер файла или настройки загрузки частями.')
  }
  const checkCancelled = () => {
    if (input.isCancelled()) throw new DOMException('Загрузка отменена', 'AbortError')
  }
  const wait = input.wait || (ms => new Promise(resolve => setTimeout(resolve, ms)))
  const send = async (body: Record<string, unknown>, cleanup = false) => {
    for (let attempt = 0; ; attempt += 1) {
      if (!cleanup) checkCancelled()
      const controller = new AbortController()
      let timedOut = false
      const timeout = setTimeout(
        () => {
          timedOut = true
          controller.abort()
        },
        cleanup ? 5000 : 60000,
      )
      const cancellation = cleanup
        ? undefined
        : setInterval(() => {
            if (input.isCancelled()) controller.abort()
          }, 200)
      try {
        const response = await input.request(
          { ...body, upload_attempt_id: input.attemptId },
          controller.signal,
        )
        if (response?.error)
          throw Object.assign(new Error(response.error.message || 'Ошибка загрузки'), {
            statusCode: response.status || 400,
          })
        if (!response?.data || typeof response.data !== 'object')
          throw new Error('Сервер вернул некорректный ответ загрузки.')
        return response
      } catch (error: any) {
        if (!cleanup) checkCancelled()
        const status = Number(error?.statusCode || 0)
        const retryable =
          timedOut || error instanceof TypeError || [408, 429, 502, 503, 504].includes(status)
        if (cleanup || attempt >= 2 || !retryable || typeof error?.payload?.raw === 'string')
          throw error
      } finally {
        clearTimeout(timeout)
        if (cancellation) clearInterval(cancellation)
      }
      await wait(500 * (attempt + 1))
    }
  }
  let uploadId: string | undefined
  try {
    const started = await send({
      action: 'start',
      name: file.name,
      mime_type: file.type || '',
      size: file.size,
      agent_id: input.agentId || null,
    })
    const session = started.data
    if (
      typeof session.upload_id !== 'string' ||
      session.chunk_bytes !== config.chunk_bytes ||
      !Number.isSafeInteger(session.next_index) ||
      session.next_index < 0 ||
      session.next_index > Math.ceil(file.size / config.chunk_bytes)
    )
      throw new Error('Сервер вернул некорректное состояние загрузки.')
    uploadId = session.upload_id
    if (session.result) return { data: session.result }
    const startedAt = Date.now()
    const initialBytes = Math.min(session.next_index * config.chunk_bytes, file.size)
    for (
      let index = session.next_index;
      index < Math.ceil(file.size / config.chunk_bytes);
      index += 1
    ) {
      checkCancelled()
      const end = Math.min((index + 1) * config.chunk_bytes, file.size)
      const buffer = await file.slice(index * config.chunk_bytes, end).arrayBuffer()
      const hash = await crypto.subtle.digest('SHA-256', buffer)
      const checksum = Array.from(new Uint8Array(hash), value =>
        value.toString(16).padStart(2, '0'),
      ).join('')
      const response = await send({
        action: 'chunk',
        upload_id: uploadId,
        index,
        checksum,
        content_base64: arrayBufferToBase64(buffer),
      })
      if (response.data.next_index !== index + 1)
        throw new Error('Сервер не подтвердил получение части файла.')
      input.onProgress(
        end,
        file.size,
        (end - initialBytes) / Math.max(0.001, (Date.now() - startedAt) / 1000),
      )
    }
    checkCancelled()
    return await send({ action: 'complete', upload_id: uploadId })
  } catch (error) {
    if (uploadId) await send({ action: 'cancel', upload_id: uploadId }, true).catch(() => undefined)
    throw error
  }
}

export const mapUploadsWithConcurrency = async <T, R>(
  items: T[],
  concurrency: number,
  run: (item: T) => Promise<R>,
): Promise<R[]> => {
  const results = new Array<R>(items.length)
  let next = 0
  await Promise.all(
    Array.from({ length: Math.min(items.length, Math.max(1, concurrency)) }, async () => {
      while (next < items.length) {
        const index = next++
        results[index] = await run(items[index])
      }
    }),
  )
  return results
}
